import { useCallback } from "react";
import { Box } from "~/components/Box";
import { EmptyState } from "~/components/EmptyState";
import { Grid } from "~/components/Grid";
import { LazyList } from "~/components/LazyList";
import { Skeleton } from "~/components/Skeleton";
import { AdvanceByReasonItem, AdvanceByReasonResponse } from "../types";
import { asDecimal, asPercent } from "../utils";
import { TableBodyCell, TableHeaderCell, TableRow } from "./dataTable";
import { PaginatedTableCard } from "./PaginatedTableCard";
import { useClientTable } from "./useClientTable";

export interface AdvanceByReasonProps {
  data?: AdvanceByReasonResponse;
  isFetching: boolean;
}

const PAGE_SIZE = 10;

const columnsTemplate = "minmax(200px, 2fr) minmax(120px, 1fr) minmax(120px, 1fr)";

const searchSelector = (item: AdvanceByReasonItem): string => item.reasonTripName;

export const AdvanceByReason = ({ data, isFetching }: AdvanceByReasonProps) => {
  const reasons = data?.reasons ?? [];
  const sumTotalOrders = reasons.reduce((sum, item) => sum + (item.totalOrders ?? 0), 0);

  const { pageRows, search, setSearch, page, setPage, totalPages, filteredCount } = useClientTable(
    reasons,
    { searchSelector, pageSize: PAGE_SIZE },
  );

  const renderBody = useCallback(() => {
    return (
      <Box css={{ overflowX: "auto" }}>
        <Grid style={{ gridTemplateColumns: columnsTemplate }}>
          <TableHeaderCell>Motivo de viagem</TableHeaderCell>
          <TableHeaderCell numeric>Antecedência média</TableHeaderCell>
          <TableHeaderCell numeric>% de voos</TableHeaderCell>

          <LazyList
            as="div"
            itemAs="div"
            css={{ display: "contents", "& > div": { display: "contents" } }}
            isLoading={isFetching || !data}
            items={pageRows}
            skeletonQuantity={10}
            EmptyComponent={
              <div style={{ gridColumn: "1 / -1" }}>
                <EmptyState>Nenhum motivo de viagem encontrado para o período.</EmptyState>
              </div>
            }
            SkeletonComponent={
              <TableRow>
                <TableBodyCell bold>
                  <Skeleton css={{ width: "70%", height: "$5" }} />
                </TableBodyCell>
                <TableBodyCell numeric>
                  <Skeleton css={{ width: "50px", height: "$5" }} />
                </TableBodyCell>
                <TableBodyCell numeric>
                  <Skeleton css={{ width: "40px", height: "$5" }} />
                </TableBodyCell>
              </TableRow>
            }
            render={(item, index) => (
              <TableRow key={`${item.reasonTripName}-${index}`}>
                <TableBodyCell bold>{item.reasonTripName}</TableBodyCell>
                <TableBodyCell numeric>{asDecimal(item.avgAdvanceDays)} dias</TableBodyCell>
                <TableBodyCell numeric>
                  {asPercent(sumTotalOrders > 0 ? item.totalOrders / sumTotalOrders : 0)}
                </TableBodyCell>
              </TableRow>
            )}
          />
        </Grid>
      </Box>
    );
  }, [isFetching, data, filteredCount, pageRows, sumTotalOrders]);

  return (
    <PaginatedTableCard
      title="Antecedência média de compra por motivo de viagem"
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Buscar motivo de viagem..."
      page={page}
      totalPages={totalPages}
      onPageChange={setPage}
      hasData={!!data}
    >
      {renderBody()}
    </PaginatedTableCard>
  );
};

AdvanceByReason.displayName = "AdvanceByReason";
